// Helpers compartidos del módulo Mantenimiento: etiquetas de dominio,
// tonos de badge por estado, formato de fechas y hooks de permisos.

import type { ReactNode } from 'react';
import type { EstadoOt, OrdenTrabajoMtto, OrigenOtLectura, TipoActivoMtto } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { ApiError } from '../../lib/api/client';
import { useMe } from '../../lib/api/queries';

// ---------------------------------------------------------------------------
// Dominio → strings UI (español formal)
// ---------------------------------------------------------------------------

export const ESTADO_OT_LABEL: Record<EstadoOt, string> = {
  abierta: 'Abierta',
  programada: 'Programada',
  en_proceso: 'En proceso',
  cerrada: 'Cerrada',
  cancelada: 'Cancelada',
};

export const ESTADO_OT_TONE: Record<EstadoOt, 'info' | 'warn' | 'accent' | 'ok' | 'neutral'> = {
  abierta: 'info',
  programada: 'warn',
  en_proceso: 'accent',
  cerrada: 'ok',
  cancelada: 'neutral',
};

/** CSS vars del tema para charts (mismo patrón que los dashboards C1/D2). */
export const ESTADO_OT_CHART_COLOR: Record<EstadoOt, string> = {
  abierta: 'var(--color-info)',
  programada: 'var(--color-warn)',
  en_proceso: 'var(--color-accent)',
  cerrada: 'var(--color-ok)',
  cancelada: 'var(--color-fg-tertiary)',
};

export const ESTADOS_OT: EstadoOt[] = [
  'abierta',
  'programada',
  'en_proceso',
  'cerrada',
  'cancelada',
];

export const TIPO_ACTIVO_LABEL: Record<TipoActivoMtto, string> = {
  sitio: 'Sitio',
  area: 'Área',
  linea: 'Línea',
  equipo: 'Equipo',
  proceso: 'Proceso',
  componente: 'Componente',
};

export const TIPOS_ACTIVO: TipoActivoMtto[] = [
  'sitio',
  'area',
  'linea',
  'equipo',
  'proceso',
  'componente',
];

// Cubre los CUATRO orígenes que la BD puede devolver, no solo los tres que el
// diálogo de creación ofrece: una OT creada desde un paro llega con
// origen 'paro' y el detalle pintaba `undefined` donde va la etiqueta.
export const ORIGEN_OT_LABEL: Record<OrigenOtLectura, string> = {
  plan: 'Plan',
  manual: 'Manual',
  solicitud: 'Solicitud',
  paro: 'Paro',
};

/** Tipos de mantenimiento sugeridos (el backend acepta texto libre ≤60). */
export const TIPOS_MANTENIMIENTO = ['preventivo', 'correctivo', 'predictivo', 'mejora'] as const;

export function tipoMantenimientoLabel(tipo: string | null): string {
  if (!tipo) return '—';
  return tipo.charAt(0).toUpperCase() + tipo.slice(1);
}

export function EstadoOtBadge({ estado }: { estado: EstadoOt }) {
  return (
    <Badge tone={ESTADO_OT_TONE[estado]} dot>
      {ESTADO_OT_LABEL[estado]}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Fechas / números (es-CO, como el resto de la app)
// ---------------------------------------------------------------------------

export function fmtFecha(iso: string | null): string {
  if (!iso) return '—';
  // 'YYYY-MM-DD' plano: evitar el shift de zona horaria de new Date('YYYY-MM-DD')
  const soloFecha = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = soloFecha ? new Date(`${iso}T00:00:00`) : new Date(iso);
  return d.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function fmtFechaHora(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtNum(value: string | number | null, decimales = 0): string {
  if (value === null) return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimales,
  });
}

/** 'YYYY-MM-DD' de hoy (fecha local del browser). */
export function hoyIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** OT vencida = fecha programada en el pasado y estado no final. */
export function otVencida(ot: Pick<OrdenTrabajoMtto, 'fecha_programada' | 'estado'>): boolean {
  return (
    ot.fecha_programada !== null &&
    ot.fecha_programada < hoyIso() &&
    ot.estado !== 'cerrada' &&
    ot.estado !== 'cancelada'
  );
}

// ---------------------------------------------------------------------------
// Permisos (dos gates del módulo: VER = capability, OPERAR = rol de módulo)
// ---------------------------------------------------------------------------

/**
 * Roles de OPERACIÓN del usuario en el módulo. El FE los usa SOLO para
 * mostrar/ocultar acciones — el gate duro sigue siendo el API (403).
 */
export function usePermisosMtto() {
  const me = useMe();
  const roles = me.data?.ctx.roles_mtto ?? [];
  const rol = me.data?.ctx.rol;
  return {
    /** Crear/editar activos y OTs, transicionar estados. */
    puedeOperar: roles.includes('coordinador_mtto') || roles.includes('tecnico'),
    /** Inventario (repuestos + movimientos): almacén también. */
    puedeOperarInventario:
      roles.includes('coordinador_mtto') || roles.includes('tecnico') || roles.includes('almacen'),
    /** Programar turnos de técnicos (grid semanal). */
    puedeProgramar: roles.includes('coordinador_mtto') || rol === 'super_admin',
    /** Pestaña Personal: gestión de usuarios del módulo + KPIs de rendimiento. */
    puedeGestionarPersonal:
      roles.includes('coordinador_mtto') || rol === 'admin_tenant' || rol === 'super_admin',
    /**
     * Análisis de paros e Informes son vistas de GESTIÓN (incluyen $ y KPIs
     * de decisión). El técnico de piso NO las ve (decisión del responsable
     * 2026-07-07); sí las ven el coordinador de mtto y los roles de gestión.
     */
    puedeVerAnalisis:
      roles.includes('coordinador_mtto') ||
      rol === 'directivo' ||
      rol === 'coordinador' ||
      rol === 'admin_tenant' ||
      rol === 'super_admin',
  };
}

// ---------------------------------------------------------------------------
// Errores del API
// ---------------------------------------------------------------------------

export function esCodigoApi(err: unknown, code: string): boolean {
  return err instanceof ApiError && err.code === code;
}

export function mensajeError(err: unknown): string {
  return err instanceof Error ? err.message : 'No pudimos completar la operación.';
}

/** Banner amable para el 403 rol_mantenimiento_no_autorizado. */
export function BannerSinRolMtto({ accion }: { accion: ReactNode }) {
  return (
    <div className="rounded border border-warn/30 bg-warn-soft px-3 py-2.5 text-xs text-fg">
      <p className="font-medium text-warn">Necesita un rol de mantenimiento</p>
      <p className="mt-0.5 text-fg-secondary">
        Su usuario puede consultar el módulo, pero para {accion} necesita un rol de operación
        (coordinador de mantenimiento, técnico o almacén). Solicite la asignación al administrador
        de su empresa.
      </p>
    </div>
  );
}
